import React, { useState, useEffect } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useChat } from "../../contexts/ChatContext";
import { formatDistanceToNow } from "date-fns";
import clsx from "clsx";

const GeneralChatSidebar = ({
  isOpen,
  onClose,
  onNewChat,
  currentSessionId,
  onSessionSelect,
}) => {
  const {
    sessions,
    sessionsLoading,
    getGeneralSessions,
    updateSession,
    deleteSession,
  } = useChat();

  const [activeDropdown, setActiveDropdown] = useState(null);
  const [editingSession, setEditingSession] = useState(null);
  const [newSessionName, setNewSessionName] = useState("");

  // Get only general sessions
  const generalSessions = getGeneralSessions();

  const handleSessionAction = async (action, sessionId, data = {}) => {
    setActiveDropdown(null);

    switch (action) {
      case "rename":
        setEditingSession(sessionId);
        const session = sessions.find((s) => s.session_id === sessionId);
        setNewSessionName(session?.session_name || "");
        break;

      case "save-rename":
        if (newSessionName.trim()) {
          await updateSession(sessionId, {
            session_name: newSessionName.trim(),
          });
        }
        setEditingSession(null);
        setNewSessionName("");
        break;

      case "cancel-rename":
        setEditingSession(null);
        setNewSessionName("");
        break;

      case "pin":
        await updateSession(sessionId, {
          is_pinned: !data.isPinned,
        });
        break;

      case "delete":
        if (
          window.confirm("Are you sure you want to delete this chat session?")
        ) {
          await deleteSession(sessionId);
        }
        break;

      default:
        break;
    }
  };

  const formatLastActivity = (timestamp) => {
    try {
      return formatDistanceToNow(new Date(timestamp), { addSuffix: true });
    } catch {
      return "Unknown";
    }
  };

  const handleSessionClick = (session, e) => {
    e.preventDefault();
    onSessionSelect(session);
    onClose?.();
  };

  const SessionItem = ({ session }) => {
    const isActive = session.session_id === currentSessionId;
    const isEditing = editingSession === session.session_id;

    return (
      <div
        className={clsx(
          "group relative rounded-lg border transition-all duration-200",
          {
            "bg-emerald-50 border-emerald-200 shadow-sm": isActive,
            "bg-white border-slate-200 hover:border-slate-300 hover:shadow-sm":
              !isActive,
          }
        )}
      >
        <button
          onClick={(e) => handleSessionClick(session, e)}
          className="block p-3 w-full text-left"
        >
          <div className="flex items-start">
            <div className="flex-1 min-w-0 mr-2">
              {isEditing ? (
                <input
                  type="text"
                  value={newSessionName}
                  onChange={(e) => setNewSessionName(e.target.value)}
                  onBlur={() =>
                    handleSessionAction("save-rename", session.session_id)
                  }
                  onKeyDown={(e) => {
                    if (e.key === "Enter") {
                      handleSessionAction("save-rename", session.session_id);
                    } else if (e.key === "Escape") {
                      handleSessionAction("cancel-rename", session.session_id);
                    }
                  }}
                  className="w-full text-sm font-medium bg-white border border-slate-300 rounded px-2 py-1 focus:outline-none focus:ring-2 focus:ring-emerald-500"
                  autoFocus
                  onClick={(e) => e.preventDefault()}
                />
              ) : (
                <h4
                  className={clsx(
                    "text-sm font-medium truncate",
                    isActive ? "text-emerald-900" : "text-slate-900"
                  )}
                >
                  <span className="flex items-center">
                    {Boolean(session.is_pinned) && (
                      <FontAwesomeIcon
                        icon="bookmark"
                        className="mr-1 text-amber-500"
                      />
                    )}
                    <span>{session.session_name}</span>
                  </span>
                </h4>
              )}

              <div className="flex items-center space-x-2 mt-1">
                <span className="text-xs text-slate-500">
                  {formatLastActivity(session.last_activity)}
                </span>
                {session.message_count > 0 && (
                  <span className="text-xs text-slate-400">
                    • {session.message_count} messages
                  </span>
                )}
              </div>
            </div>

            {/* Icons container */}
            <div className="flex items-center space-x-1 flex-shrink-0">
              {/* General chat icon */}
              <div className="p-1 rounded bg-emerald-100 text-emerald-600">
                <FontAwesomeIcon icon="robot" className="text-xs" />
              </div>

              {/* Options button */}
              {!isEditing && (
                <button
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    setActiveDropdown(
                      activeDropdown === session.session_id
                        ? null
                        : session.session_id
                    );
                  }}
                  className="px-2 py-1 rounded hover:bg-slate-200 transition-colors duration-200"
                  title="More options"
                >
                  <FontAwesomeIcon
                    icon="ellipsis-v"
                    className="text-slate-400 hover:text-slate-600 text-sm"
                  />
                </button>
              )}
            </div>
          </div>
        </button>

        {/* Session actions dropdown */}
        {!isEditing && activeDropdown === session.session_id && (
          <div className="absolute right-0 top-10 w-40 bg-white border border-slate-200 rounded-lg shadow-lg z-10">
            <button
              onClick={() => handleSessionAction("rename", session.session_id)}
              className="w-full text-left px-3 py-2 text-sm text-slate-700 hover:bg-slate-100 rounded-t-lg"
            >
              <FontAwesomeIcon icon="edit" className="mr-2 text-xs" />
              Rename
            </button>
            <button
              onClick={() =>
                handleSessionAction("pin", session.session_id, {
                  isPinned: session.is_pinned,
                })
              }
              className="w-full text-left px-3 py-2 text-sm text-slate-700 hover:bg-slate-100"
            >
              <FontAwesomeIcon
                icon={Boolean(session.is_pinned) ? "bookmark" : "bookmark"}
                className={clsx(
                  "mr-2 text-xs",
                  Boolean(session.is_pinned) ? "text-amber-500" : ""
                )}
              />
              {Boolean(session.is_pinned) ? "Unpin" : "Pin"}
            </button>
            <button
              onClick={() => handleSessionAction("delete", session.session_id)}
              className="w-full text-left px-3 py-2 text-sm text-red-600 hover:bg-red-50 rounded-b-lg"
            >
              <FontAwesomeIcon icon="trash" className="mr-2 text-xs" />
              Delete
            </button>
          </div>
        )}
      </div>
    );
  };

  return (
    <>
      {/* Mobile overlay */}
      {isOpen && (
        <div
          className="lg:hidden fixed inset-0 bg-black bg-opacity-50 z-40"
          onClick={onClose}
        />
      )}

      {/* Sidebar */}
      <div
        className={clsx(
          "w-80 bg-slate-50 border-r border-slate-200 flex flex-col transition-transform duration-300 ease-in-out lg:relative lg:translate-x-0",
          {
            "translate-x-0": isOpen,
            "-translate-x-full": !isOpen,
            "fixed inset-y-0 left-0 z-50 lg:static": true,
          }
        )}
      >
        {/* Header */}
        <div className="p-4 border-b border-slate-200 bg-white">
          <div className="flex items-center justify-between mb-3">
            <h2 className="font-semibold text-slate-900">General Chats</h2>
            <button
              onClick={onClose}
              className="lg:hidden p-1 rounded-lg hover:bg-slate-100 transition-colors duration-200"
            >
              <FontAwesomeIcon icon="times" className="text-slate-500" />
            </button>
          </div>

          {/* New Chat Button */}
          <button
            onClick={onNewChat}
            className="w-full flex items-center justify-center space-x-2 py-3 px-4 text-white rounded-lg transition-all duration-200 shadow-sm hover:shadow-md font-medium bg-emerald-500 hover:bg-emerald-600"
          >
            <FontAwesomeIcon icon="plus" />
            <span>New General Chat</span>
          </button>
        </div>

        {/* Sessions list */}
        <div className="flex-1 overflow-y-auto p-4">
          {sessionsLoading ? (
            <div className="flex items-center justify-center py-8">
              <FontAwesomeIcon
                icon="spinner"
                className="animate-spin text-slate-400"
              />
            </div>
          ) : generalSessions.length > 0 ? (
            <div className="space-y-2">
              {generalSessions
                .sort((a, b) => {
                  // Sort pinned first, then by last activity
                  if (a.is_pinned !== b.is_pinned) {
                    return b.is_pinned - a.is_pinned;
                  }
                  return new Date(b.last_activity) - new Date(a.last_activity);
                })
                .map((session) => (
                  <SessionItem key={session.session_id} session={session} />
                ))}
            </div>
          ) : (
            <div className="text-center py-8">
              <FontAwesomeIcon
                icon="comment"
                className="text-4xl text-slate-300 mb-3"
              />
              <p className="text-slate-500 text-sm mb-4">
                No general chat sessions yet.
              </p>
              <button
                onClick={onNewChat}
                className="text-sm text-emerald-600 hover:text-emerald-800"
              >
                Start your first conversation!
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Click outside to close dropdown */}
      {activeDropdown && (
        <div
          className="fixed inset-0 z-30"
          onClick={() => setActiveDropdown(null)}
        />
      )}
    </>
  );
};

export default GeneralChatSidebar;
